import { useMutation } from '@apollo/client';
import { Button, FieldInput, Meta, Spinner } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import { UNLOCK_LOCKED_SEGMENT_V2 } from 'graphql/mutations/unlockLockedSerment';
import { UNLOCK_URL_LOCKED_SEGMENT } from 'graphql/mutations/unlockLockedUrlSegment';
import { GET_CUSTOMER } from 'graphql/queries/getCustomer';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useHasUnlockedCustomerSegment from 'hooks/useHasUnlockedCustomerSegment';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeviceInfo from 'react-native-device-info';
import WebView, { WebViewNavigation } from 'react-native-webview';
import { urlUnlockingSegment } from 'states/persistInStorage';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationUnlockLockedSegmentV2Args,
  MutationUnlockUrlLockedSegmentArgs,
  UnlockSegmentInputV2,
} from 'types/generatedSchemaTypes';
import { getLang } from 'utils/commonUtils';
import { isAndroid, isIos, isWeb } from 'utils/deviceUtils';
import { unlockUrlTariffProviders } from 'utils/tariffUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { Center, Row } from '../styles/styles';
import { useUiStore } from 'stores/uiStore';

const Container = styled.View`
  height: 100%;
  padding-bottom: 40px;
`;

const InputWrapper = styled(StyledWrapper)<{ mt: number }>`
  margin-top: ${(p) => p.mt}px;
`;

const UnlockTariffModalContent: React.FC = () => {
  const [code, setCode] = useState('');
  const parkingZoneUid = useActiveElementStore((state) => state.activeElement);
  const charger = useActiveElementStore((state) => state.activeCharger);
  const unlockTariff = useParkzoneStore((state) => state.unlockTariffItem);
  const instructions = unlockTariff?.accessMethod?.instructions;
  const [logoutUser, setLogoutUser] = useState<boolean>(false);
  const { t } = useTranslation();
  const { pricingSchemeId, pricingCountryCode } =
    useParkingPricingInfo(parkingZoneUid);
  const { hasUnlocked } = useHasUnlockedCustomerSegment(
    unlockTariff?.tariffId || '',
    pricingCountryCode || 'SE',
    unlockTariff?.zoneCode
  );
  const [manufacturer, setManufacturer] = useState('');
  const urlUnlockingProvider = unlockUrlTariffProviders(
    unlockTariff?.accessMethod?.url as string
  );
  const { isLoggedIn } = useIsLoggedIn();
  const { navigateToAuthentication, setReturnRedirectToBuyShortTermParking } =
    useAfterAuthenticationRedirect();

  const setUnlockedTariff = () => {
    if (unlockTariff?.activeElementType === 'PARKING') {
      useParkingStore.getState().setHasManuallySelectedTariff(true);
      useParkzoneStore.getState().setSelectedTariffId(unlockTariff?.tariffId);
      useParkzoneStore
        .getState()
        .setSelectedTariffName(unlockTariff?.tariffName);
    }
    if (unlockTariff?.activeElementType === 'CHARGING') {
      useParkzoneStore
        .getState()
        .setSelectedChargingPricingInfoSegment(unlockTariff?.chargingTariff);
      useParkzoneStore
        .getState()
        .setSelectedChargingPricingInfoSegmentTariffPlanId(
          unlockTariff?.chargingTariff?.tariffPlanId
        );
    }
  };

  const [unlockSegement, { loading }] = useMutation<
    Mutation,
    MutationUnlockLockedSegmentV2Args
  >(UNLOCK_LOCKED_SEGMENT_V2, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        errorPolicy: 'all',
      },
    ],
    onCompleted: () => {
      showToast('success.tariffUnlock', 'success');
      setUnlockedTariff();
      useUiStore.getState().setSheetModal('');
    },
    onError: (error) => {
      const errorToastMap: Record<string, string> = {
        CODE_ALREADY_USED: 'error.tariffUnlockCodeAlreadyUsed',
        CODE_EXPIRED: 'error.tariffUnlockCodeExpired',
        INVALID_CODE: 'error.tariffUnlockInvalidCode',
      };
      const matchedCode = error.graphQLErrors.find(
        (e) => errorToastMap[e.extensions?.code as string]
      )?.extensions?.code as string;
      showToast(
        errorToastMap[matchedCode] ?? 'error.tariffUnlockFailed',
        'error'
      );
    },
  });

  const [unlockUrlSegment, { loading: urlUnlockLoading }] = useMutation<
    Mutation,
    MutationUnlockUrlLockedSegmentArgs
  >(UNLOCK_URL_LOCKED_SEGMENT, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        errorPolicy: 'all',
      },
    ],
    onCompleted: () => {
      setLogoutUser(true);
      showToast('success.tariffUnlock', 'success');
      setUnlockedTariff();
    },
    onError: () => {
      setLogoutUser(true);
      showToast('error.tariffUnlockFailed', 'error');
    },
  });

  const handleUnlock = debounce(
    () => {
      if (
        unlockTariff?.activeElementType === 'CHARGING' &&
        !charger?.chargingLocationId
      ) {
        showToast('error.noChargerSelected', 'error');
      } else {
        const input: UnlockSegmentInputV2 = {
          code,
          customerSegmentUid: unlockTariff?.tariffId,
          zoneCode: unlockTariff?.zoneCode,
          hasUnlocked,
          countryCode: pricingCountryCode,
        };

        if (unlockTariff?.activeElementType === 'CHARGING') {
          input.chargingLocationId = charger?.chargingLocationId;
        } else {
          input.parkingZoneUid = parkingZoneUid;
        }

        unlockSegement({
          variables: {
            input,
          },
        });
      }
    },
    1,
    { leading: !isWeb }
  );

  const handleSignIn = async () => {
    useUiStore.getState().setSheetModal('');
    setReturnRedirectToBuyShortTermParking(parkingZoneUid);
    await navigateToAuthentication(isWeb);
  };

  const handleUrlChange = async (url: string) => {
    if (url.includes(urlUnlockingProvider?.logoutUrl as string)) {
      setTimeout(() => {
        useUiStore.getState().setSheetModal('');
      }, 500);
    }
    if (
      url.includes(urlUnlockingProvider?.responseCheck as string) &&
      !logoutUser
    ) {
      const token = url.substring(url.indexOf('=') + 1, url.length);
      if (token) {
        return unlockUrlSegment({
          variables: {
            input: {
              token,
              pricingSchemeId,
              zoneCode: unlockTariff?.zoneCode as string,
              customerSegmentUid: unlockTariff?.tariffId as string,
              service: urlUnlockingProvider?.service,
              provider: urlUnlockingProvider?.provider,
              hasUnlocked,
            },
          },
        });
      }
    }
  };

  const onNavigationStateChange = async ({ url }: WebViewNavigation) => {
    await handleUrlChange(url);
  };

  const getAuthUrl = () => {
    if (
      unlockTariff?.accessMethod?.type === 'URL' &&
      unlockTariff.accessMethod.url !== ''
    ) {
      return !logoutUser
        ? (urlUnlockingProvider?.loginUrl as string)
        : (urlUnlockingProvider?.logoutUrl as string);
    }
    return '';
  };

  const hasInstructions = !!(instructions && instructions[getLang()]);

  useEffect(() => {
    if (!isWeb) {
      DeviceInfo.getManufacturer().then(setManufacturer);
    }
  }, [setManufacturer]);

  const extraHeight = useMemo(() => {
    if (
      isAndroid &&
      manufacturer.toLowerCase() === 'samsung' &&
      !hasInstructions
    ) {
      return 25;
    }
    return 0;
  }, [hasInstructions, manufacturer]);

  if (loading || urlUnlockLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const debouncedHandleChange = debounce(
    (text) => {
      setCode(text);
    },
    1,
    { leading: !isWeb }
  );

  const handleChangeText = (text: string) => {
    debouncedHandleChange(text);
  };

  const webviewRenderer = () => {
    if (!isWeb) {
      return (
        <WebView
          useWebKit
          height={'100%'}
          testID="url-unlocked-segment-login"
          nestedScrollEnabled={true}
          source={{
            uri: getAuthUrl(),
          }}
          onNavigationStateChange={onNavigationStateChange}
        />
      );
    }
    if (pricingSchemeId) {
      urlUnlockingSegment({
        hasUnlocked,
        pricingScheme: pricingSchemeId as string,
        zoneCode: unlockTariff?.zoneCode,
        customerSegmentUid: unlockTariff?.tariffId,
        service: urlUnlockingProvider?.service,
        provider: urlUnlockingProvider?.provider,
        logoutUrl: urlUnlockingProvider?.logoutUrl,
      });
      (window as any).open(getAuthUrl(), '_self');
    }
  };

  if (!isLoggedIn) {
    return (
      <StyledWrapper center={isWeb} maxWidth={isWeb ? 450 : undefined}>
        <Button
          {...testIdentifiers('unlock-tariff-code-login')}
          text={t('parking.notSignedIn.button.long')}
          size="l"
          buttonType="secondary"
          onPress={handleSignIn}
        />
      </StyledWrapper>
    );
  }

  if (unlockTariff?.accessMethod?.type === 'URL') {
    return <Container>{webviewRenderer()}</Container>;
  }

  return (
    <StyledWrapper>
      {hasInstructions && (
        <Row mb={50} mt={20}>
          <Meta testID="unlock-tariff-instructions">
            {instructions[getLang()]}
          </Meta>
        </Row>
      )}
      <InputWrapper
        mb={30 + extraHeight}
        center={isWeb}
        mt={extraHeight}
        maxWidth={isWeb ? 450 : undefined}>
        <FieldInput
          title={t('parking.tariff.unlockCode')}
          value={code}
          keyboardType="default"
          onChangeText={handleChangeText}
          isSheet={isIos}
          testID="unlock-tariff-code-input"
        />
      </InputWrapper>
      <StyledWrapper center={isWeb} maxWidth={isWeb ? 450 : undefined}>
        <Button
          loading={loading}
          {...testIdentifiers('unlock-tariff-code-button')}
          text={t('parking.tariff.unlock')}
          size="l"
          buttonType="secondary"
          onPress={handleUnlock}
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default UnlockTariffModalContent;
